import React, { useEffect, useState } from 'react';
// import { ReactComponent as StarIcon } from '../../../icons/StarIcon.svg';
import { ReactComponent as NavResourcesIconDark } from '../../../icons/NavResourcesIconDark.svg';
import { useLoadCommunities } from '../../../hooks/apphooks';
import { CommunitySchema } from '../../../data/types';
import CommunityCardSkeleton from '../../../components/CommunityCardSkeleton';
import { CommunityResourcesModal } from '../../../molecules/Desktop/AppModal';

// import { CommunityResourcesModal as MobileCommunityResourcesModal } from '../../../molecules/Mobile/AppModal';
// import { useMediaQuery } from '@mui/material';

interface CommunityCardProps {
  logo?: string;
  name: string;
  address: string;
  description: string;
  // resourceCount: number;
  // communityId?: string;
  onViewResources?: () => void;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  logo,
  name,
  address,
  description,
  // resourceCount,
  // communityId,
  onViewResources,
}) => {
  const [showDefaultAvatar, setShowDefaultAvatar] = useState(!logo);

  // Generate initials from community name
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const handleImageError = () => {
    setShowDefaultAvatar(true);
  };

  // Reset error state if logo changes
  useEffect(() => {
    if (logo) {
      setShowDefaultAvatar(false);
    } else {
      setShowDefaultAvatar(true);
    }
  }, [logo]);

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 flex flex-col h-full border border-gray-100 hover:border-gray-200 group cursor-pointer">
      {/* Logo Section */}
      <div className="flex items-start mb-5">
        <div className="w-16 h-16 border border-[#E9E9E9]/70 rounded-xl flex items-center justify-center p-1 bg-gradient-to-br from-gray-50 to-white shadow-sm group-hover:shadow-md transition-shadow duration-300 overflow-hidden">
          {logo && !showDefaultAvatar ? (
            <img 
              src={logo} 
              alt={name} 
              className="w-full h-full object-contain rounded-lg" 
              onError={handleImageError}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primaryDark-400 to-primaryDark-300 rounded-xl">
              <span className="text-white font-raleway font-bold text-xl leading-none">
                {getInitials(name)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Community Name */}
      <h3 className="font-raleway font-bold text-gray-900 text-xl mb-3 leading-tight group-hover:text-primaryDark-400 transition-colors duration-300">
        {name}
      </h3>

      {/* Description */}
      {description && (
        <p className="font-open-sans text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed flex-grow">
          {description}
        </p>
      )}

      {/* Address with Icon */}
      {address && (
        <div className="flex items-start gap-2 mb-5">
          <svg 
            className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
            />
          </svg>
          <p className="font-open-sans text-gray-500 text-sm leading-relaxed flex-1">
            {address}
          </p>
        </div>
      )}

      {/* Resources Button */}
      <div className="mt-auto pt-2">
        <button 
          onClick={onViewResources}
          className="w-full flex items-center justify-center gap-2 bg-gray-50 hover:bg-primaryDark-400 text-gray-700 hover:text-white font-open-sans font-medium text-sm rounded-lg px-4 py-3 transition-all duration-300 border border-[#E9E9E9] hover:border-0 group-hover:shadow-sm"
        >
          {/* <svg 
            className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" 
            />
          </svg> */}
          <NavResourcesIconDark className="my-auto mr-1 text-[#013847]/50 " />
          <span className="font-raleway text-sm font-medium my-auto text-[#013847]">View Resources</span>
        </button>
      </div>
    </div>
  );
};

const PartnerCommunities: React.FC = () => {
  // Mock data for communities - in production, this would come from an API
  const {allCommunities: communities, isLoadingCommunities} = useLoadCommunities()
  // console.log(communities)

  const [communityResourceID, setCommunityResourceID] = useState("");
  const [viewCommunityResources, setViewCommunityResources] = useState(false);

  const handleSetViewCommunityResources = (communityId: string, isOpen: boolean) => {
    setViewCommunityResources(isOpen);
    setCommunityResourceID(communityId);
  };
  // const communities: CommunityCardProps[] = [
  //   {
  //     name: 'Union Kitchen',
  //     address: '1231-B Marion Barry Ave SE Washington, DC 20020',
  //     logo: 'https://via.placeholder.com/150',
  //     kitchenSpaces: 30,
  //     eventSpaces: 4,
  //     documentations: 100,
  //     moreCount: 3,
  //   },
  //   {
  //     name: 'Dreaming Out Loud',
  //     address: '1231-B Marion Barry Ave SE Washington, DC 20020',
  //     logo: 'https://via.placeholder.com/150',
  //     kitchenSpaces: 30,
  //     eventSpaces: 4,
  //     documentations: 100,
  //     moreCount: 3,
  //   },
  //   {
  //     name: 'Kitchen Of Purpose',
  //     address: '1231-B Marion Barry Ave SE Washington, DC 20020',
  //     logo: 'https://via.placeholder.com/150',
  //     kitchenSpaces: 30,
  //     eventSpaces: 4,
  //     documentations: 100,
  //     moreCount: 3,
  //   },
  // ];

  const [displayedCommunities, setDisplayedCommunities] = React.useState(communities?.slice(0, 6) || []);
  const [hasMore, setHasMore] = React.useState(communities?.length && communities.length > 6);

  const handleLoadMore = () => {
    // In production, this would fetch more communities from an API
    // For now, we'll just show that there are no more to load
    setHasMore(false);
  };

  useEffect(() => {
    document.title = `HomeCook | Communities`;
    setDisplayedCommunities(communities?.slice(0, 6) || []);
    setHasMore(communities?.length && communities.length > 6);
  }, [communities]);
  // Check if the view is on mobile
  // const isMobileView = useMediaQuery('(max-width: 768px)');

  return (
    <div className="min-h-screen bg-[#F8F8F8] py-12 px-4 sm:px-6 lg:px-8 top-10 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-raleway font-bold text-3xl sm:text-4xl">
            <span className="text-primaryDark-200">Major </span>
            <span className="text-primaryDark-400">Communities </span>
            <span className="text-primaryDark-0">Behind <br/>Local </span>
            <span className="text-primaryDark-400">Food Businesses.</span>
          </h1>
        </div>

        {/* Community Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {isLoadingCommunities ? (
            // Show skeleton loaders while communities are being fetched
            Array.from({ length: 6 }).map((_, index) => (
              <CommunityCardSkeleton key={`skeleton-${index}`} />
            ))
          ) : displayedCommunities && displayedCommunities.length > 0 ? (
            displayedCommunities.map((community: CommunitySchema, index: number) => (
              <CommunityCard 
                key={index} 
                name={community?.name || ''} 
                description={community?.description || ''} 
                address={community?.address || ''} 
                logo={community?.logo || ''} 
                // resourceCount={(community as CommunitySchema)?.resource_count || 0}
                // communityId={community?.id}
                onViewResources={() => handleSetViewCommunityResources(community?.id || '', true)}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-[#013847]/60">
              No communities to show
            </div>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMore}
              className="bg-primaryDark-400 border-0 hover:bg-primaryDark-300 text-white font-raleway font-semibold py-3 px-8 rounded-lg transition-colors shadow-sm"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      <CommunityResourcesModal 
        isOpen={viewCommunityResources} 
        communityId={communityResourceID} 
        closeAppModal={() => setViewCommunityResources(false)} 
      />
    </div>
  );
};

export default PartnerCommunities;